/**
 * ============================================================================
 *  File        : frontend/src/api/workflow.ts
 *  Module      : api
 *  Purpose     : 案件 lifecycle 脊柱（S-spine）型別化 API — workflow-state 查詢
 *                + 後段 transition 驅動命令（final-weight/receive、final-report/review、close）
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：crates/api-http/src/routes/v13_case_spine.rs，掛在 v1：
 *    GET  /api/v1/cases/:id/workflow-state          -> workflow_state（marketplace.cases.view）
 *    POST /api/v1/cases/:id/final-weight/receive    -> receive_final_weight
 *                                                      （P2PaymentConfirmed → FinalWeightReceived）
 *    POST /api/v1/cases/:id/final-report/review     -> open_final_report_review
 *                                                      （FinalWeightReceived → FinalReportReview）
 *    POST /api/v1/cases/:id/close                   -> close
 *                                                      （FinalReportReview → ClosedLocked）
 *  spine mutation RBAC：final-weight/receive = marketplace.settlements.calculate；
 *  final-report/review + close = marketplace.cases.update（gate 由後端 v1.rs 套用）。
 *  每個 mutation 必帶 Idempotency-Key（mutationHeaders 自動產 UUID）。
 *  ClosedLocked 後再呼叫 close → 後端回 409 CASE_STATE_TRANSITION_INVALID（凍結）。
 */

import { API_BASE, mutationHeaders, requestData } from './client'

/** 案件 workflow 狀態（= 後端 v13_case_workflow workflow_state 的 data）。 */
export interface WorkflowStateDto {
  case_id: number
  /** 目前狀態機狀態（如 P2PaymentConfirmed / FinalWeightReceived / FinalReportReview / ClosedLocked）。 */
  workflow_state: string
  /** 是否為阻塞態（ClosedLocked / AuditRequired 等業務資料鎖定）。 */
  is_blocking: boolean
  /** 允許的下一步狀態集合（空 = 無可進行的 transition，例如 ClosedLocked）。 */
  allowed_next: string[]
  /** 是否有待重算（重量裁定後 settlement 需重跑）。 */
  has_recalculation_pending: boolean
}

/** spine transition 結果（= 後端 SpineTransitionResultDto）。 */
export interface SpineTransitionResultDto {
  case_id: number
  workflow_state: string
}

const CASES_PATH = `${API_BASE}/cases`

/** 取得案件目前 workflow 狀態（驅動後段畫面的權威狀態來源）。 */
export function getWorkflowState(caseId: number | string): Promise<WorkflowStateDto> {
  return requestData<WorkflowStateDto>(`${CASES_PATH}/${caseId}/workflow-state`, { method: 'GET' })
}

/**
 * 出荷確定（DetailAccepted → Shipped）。
 * 後端：POST /api/v1/cases/:id/ship（v13_case_spine::ship，RBAC = logistics.shipments.manage）。
 * 執行中 binary 已驗 2026-06-01：DetailAccepted 時呼叫 → workflow_state=Shipped。
 */
export function shipCase(caseId: number | string): Promise<SpineTransitionResultDto> {
  return requestData<SpineTransitionResultDto>(`${CASES_PATH}/${caseId}/ship`, {
    method: 'POST',
    headers: mutationHeaders(),
  })
}

/** 最終出荷重量受領推進（P2PaymentConfirmed → FinalWeightReceived）。 */
export function receiveFinalWeight(caseId: number | string): Promise<SpineTransitionResultDto> {
  return requestData<SpineTransitionResultDto>(`${CASES_PATH}/${caseId}/final-weight/receive`, {
    method: 'POST',
    headers: mutationHeaders(),
  })
}

/** 最終報告レビュー開始（FinalWeightReceived → FinalReportReview）。 */
export function openFinalReportReview(
  caseId: number | string,
): Promise<SpineTransitionResultDto> {
  return requestData<SpineTransitionResultDto>(`${CASES_PATH}/${caseId}/final-report/review`, {
    method: 'POST',
    headers: mutationHeaders(),
  })
}

/**
 * 結案ロック（FinalReportReview → ClosedLocked）— 鎖定後業務資料 100% 不可改。
 * 高風險端點（spec §9.2）:需帶 2FA step-up `stepUpCode`（→ `X-2FA-Code` header）。
 */
export function closeCase(
  caseId: number | string,
  stepUpCode?: string,
): Promise<SpineTransitionResultDto> {
  return requestData<SpineTransitionResultDto>(`${CASES_PATH}/${caseId}/close`, {
    method: 'POST',
    headers: mutationHeaders(stepUpCode),
  })
}
